import { LICENSE_MODULE } from "@selector/shared";
import pluralize from "pluralize";

import { type IInfo } from "@/api/WebApiClient.ts";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { capitalizeFirstLetter } from "@/app-utils/string.ts";
import { type Configuration, type Playbook, type SearchAttribute, type SystemParameter } from "@/graphql.ts";
import {
  LOAD_APP_INFO_FAILURE,
  LOAD_APP_INFO_REQUEST,
  LOAD_APP_INFO_SUCCESS,
  LOAD_CONFIG_FAILURE,
  LOAD_CONFIG_REQUEST,
  LOAD_CONFIG_SUCCESS,
} from "@/redux/action-types.ts";
import { PLAYBOOK_TYPE, SYSTEM_PARAMS } from "@/settings/settings.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { createReducer } from "../redux-utils.js";
import type { ITableColumn } from "./table-data.js";

export interface IConfigurationState {
  isLoadingConfiguration: boolean;
  isLoadingAppInfo: boolean;
  configuration: Configuration;
}

const initialState: IConfigurationState = {
  isLoadingConfiguration: false,
  isLoadingAppInfo: true, // important that it is true upon initialisation
  configuration: null,
};

export interface ILicensable {
  licenseModule?: LICENSE_MODULE; // in order to be able to filter by licensed module
  isClassicProcessor?: boolean; // to filter out disabled items
}

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_CONFIG_REQUEST]: (state: IConfigurationState) => {
    return {
      ...state,
      isLoadingConfiguration: true,
    };
  },
  [LOAD_CONFIG_SUCCESS]: (state: IConfigurationState, config: Configuration) => {
    return {
      ...state,
      isLoadingConfiguration: false,
      configuration: { ...state.configuration, ...config },
    };
  },
  [LOAD_CONFIG_FAILURE]: (state: IConfigurationState) => {
    return {
      ...state,
      isLoadingConfiguration: false,
    };
  },

  [LOAD_APP_INFO_REQUEST]: (state: IConfigurationState) => {
    return {
      ...state,
      isLoadingAppInfo: true,
    };
  },
  [LOAD_APP_INFO_SUCCESS]: (state: IConfigurationState, config: Configuration) => {
    return {
      ...state,
      isLoadingAppInfo: false,
      configuration: { ...state.configuration, ...config },
    };
  },
  [LOAD_APP_INFO_FAILURE]: (state: IConfigurationState) => {
    return {
      ...state,
      isLoadingAppInfo: false,
    };
  },
});

/**
 * Action creators
 */

export function loadInfo() {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({
      type: LOAD_APP_INFO_REQUEST,
    });

    return webApiClient
      .fetchInfo()
      .then((config: IInfo) => {
        // config.application.hpcEnabled = true;
        dispatch({
          type: LOAD_APP_INFO_SUCCESS,
          payload: config,
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_APP_INFO_FAILURE,
          alertTitle: "Fetching of App Info Failed",
        }),
      );
  };
}

export function loadConfiguration() {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch({
      type: LOAD_CONFIG_REQUEST,
    });

    return webApiClient
      .fetchConfiguration()
      .then((config: Configuration) => {
        setSystemParamsFromConfiguration(config.systemParameters);
        dispatch({
          type: LOAD_CONFIG_SUCCESS,
          payload: config,
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_CONFIG_FAILURE,
          alertTitle: "Fetching of Configuration Failed",
        }),
      );
  };
}

/**
 Utils
 */
function setSystemParamsFromConfiguration(systemParameters: SystemParameter) {
  if (!systemParameters) {
    return;
  }
  Object.keys(systemParameters).forEach((systemParamKey: string) => {
    SYSTEM_PARAMS[systemParamKey] = systemParameters[systemParamKey];
  });
}

export function filterPlaybooksByLicense(playbooks: Playbook[], configuration: Configuration): Playbook[] {
  return playbooks.filter((playbook: Playbook) => isPlaybookLicensed(configuration, playbook));
}

export function filterByLicense<T extends ILicensable>(items: T[], configuration: Configuration): T[] {
  return items.filter((item: T) => !item.licenseModule || isLicensed(configuration, item.licenseModule));
}

export function filterByClassicProcessorDisabled<T extends ILicensable>(items: T[], configuration: Configuration): T[] {
  return items.filter((item: T) => !(item.isClassicProcessor && isClassicProcessorDisabled(configuration)));
}

export const isPlaybookLicensed = (configuration: Configuration, playbook: Playbook): boolean => {
  if (playbook?.type == null) return false;
  switch (playbook?.type.id) {
    case PLAYBOOK_TYPE.BLAST:
    case PLAYBOOK_TYPE.REPORT_FROM_VARIANTS:
    case PLAYBOOK_TYPE.REPORT_FROM_COORDINATES:
      return isLicensed(configuration, LICENSE_MODULE.EXPLORER);
    case PLAYBOOK_TYPE.NGS_ANALYSIS:
    case PLAYBOOK_TYPE.RANGE_ANALYSIS:
      return isLicensed(configuration, LICENSE_MODULE.PROCESSOR);
    case PLAYBOOK_TYPE.SAMPLE_REGISTRATION:
      return isLicensed(configuration, LICENSE_MODULE.TRACKER);
    case PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES:
    case PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES:
      return isLicensed(configuration, LICENSE_MODULE.TRACKER) && isLicensed(configuration, LICENSE_MODULE.PROCESSOR);
    default:
    // console.error(`Could not make sure the playbook with type '${playbook?.type.id}' is licensed!`);
  }
  return true;
};

export const isClassicProcessorDisabled = (configuration: Configuration) => {
  return configuration?.application?.disableClassicProcessor;
};

export const isClassicUiDisabled = (configuration: Configuration) => {
  return configuration?.application?.disableClassicUi;
};

export const isLicensed = (configuration: Configuration, module: LICENSE_MODULE): boolean => {
  if (configuration?.license?.modules) {
    return configuration.license.modules.includes(module);
  }
  return false;
};

export function getProjectLabel(capitalizeFirst = false, plural = false) {
  const label: string = plural ? pluralize.plural(SYSTEM_PARAMS.projectLabel) : SYSTEM_PARAMS.projectLabel;

  return capitalizeFirst ? capitalizeFirstLetter(label) : label;
}

// brute-force replacement of "project" by the project label defined in the system parameter
export function replaceProjectTermWithSystemSetting(value: string): string {
  return value.replace(/project/g, getProjectLabel(false, false)).replace(/Project/g, getProjectLabel(true, false));
}

export function parseTableColumnAndReplaceProjectTermWithSystemSetting(tableColumn: ITableColumn): ITableColumn {
  return tableColumn.field === "project.name"
    ? {
        ...tableColumn,
        name: tableColumn.name ? replaceProjectTermWithSystemSetting(tableColumn.name) : undefined,
        searchAttributes: tableColumn.searchAttributes
          ? parseSearchAttributeAndReplaceProjectTermWithSystemSetting(tableColumn.searchAttributes)
          : undefined,
      }
    : tableColumn;
}

export function parseSearchAttributeAndReplaceProjectTermWithSystemSetting(
  searchAttribute: SearchAttribute,
): SearchAttribute {
  return searchAttribute.field === "project.name"
    ? {
        ...searchAttribute,
        name: searchAttribute.name ? replaceProjectTermWithSystemSetting(searchAttribute.name) : undefined,
      }
    : searchAttribute;
}
